/* eslint-disable react/prop-types */
import { useMemo, useState } from 'react';
import { classNames } from '@/shared/lib/classNames';
import { SkeletonWrapper } from '@/features/Skeleton';
import { DataNotFound } from '@/features/DataNotFound';
import { useResize } from '@/shared/lib/hooks/useResize/useResize';
import { ChartControls, type QueryFilter } from '@/features/ChartControls';
import { getChartTooltipDate, getXAxisLabel } from '@/features/TripleChart/lib/dates';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { MAX_DATE, MIN_DATE } from '../model/data';
import { getMinMaxValue } from '../lib/getMinMaxValue';
import { useChartData } from '../lib/query/useChartData';
import cls from './SingleChart.module.scss';

type Props = {
    calendarOffset: [number, number];
    chartContainerClassName?: string;
    chartControlsClassName?: string;
    chartControlsSize: 'l' | 'm' | 's' | 'xs';
    chartOffset?: number;
    chartTicksClassName?: string;
    className?: string;
    lastCouponClassName?: string;
    legendClassName?: string;
    squareClassName?: string;
    yAxisOffset?: number;
};

export const SingleChart = ({
    chartControlsClassName,
    className,
    calendarOffset,
    chartContainerClassName,
    lastCouponClassName,
    chartControlsSize,
    chartTicksClassName,
    legendClassName,
    squareClassName,
    chartOffset: externalChartOffset,
    yAxisOffset: externalYAxisOffset,
}: Props) => {
    const [selectedTimeFrame, setSelectedTimeFrame] = useState<QueryFilter>({ segmentsValue: 'all', timeRange: null });
    const [isTooltipShown, setIsTooltipShown] = useState(false);

    const { chartData, isLoading } = useChartData({ filter: selectedTimeFrame });

    const { min, max } = useMemo(() => {
        if (!chartData || !chartData.length) {
            return {
                min: 0,
                max: 100,
            };
        }

        const { min, max } = getMinMaxValue({ chartPoints: chartData });

        return {
            min,
            max,
        };
    }, [chartData]);

    const ticks = useMemo(() => {
        if (!chartData || !chartData.length) {
            return [];
        }

        const dates = chartData.map((el) => el.quotesDate);

        if (
            !selectedTimeFrame.segmentsValue ||
            selectedTimeFrame.segmentsValue === '3m' ||
            selectedTimeFrame.segmentsValue === '6m'
        ) {
            return dates;
        }

        if (selectedTimeFrame.segmentsValue === 'y') {
            return dates.filter((_date, index) => {
                return index === 0 || index === dates.length - 1 || index % 2 === 0;
            });
        }

        if (selectedTimeFrame.segmentsValue === 'all') {
            const divider = chartData.length / 6;

            return dates.filter((_date, index) => {
                return index === 0 || index === dates.length - 1 || index % divider === 0;
            });
        }
    }, [selectedTimeFrame.segmentsValue, chartData]);

    const showChart = Boolean(chartData?.length || isLoading);
    const showLastCoupon = showChart && !isLoading;

    const width = useResize();
    const chartOffset = externalChartOffset ?? (width > 768 ? 20 : 0);
    const yAxisOffset = externalYAxisOffset ?? (width > 768 ? 16 : 10);
    const legendPaddingLeft = width > 768 ? 0 : 30;

    return (
        <div className={cls.wrapper}>
            {showLastCoupon && (
                <div className={classNames(cls.lastCoupon, {}, [lastCouponClassName])}>
                    <p className={cls.lastCouponTitle}>{chartData![chartData!.length - 1]!.quote.toLocaleString('ru-ru')} ₽</p>
                    <p className={cls.lastCouponText}>на {getXAxisLabel(chartData![chartData!.length - 1]!.quotesDate)}</p>
                </div>
            )}
            <div className={classNames('singleChart', {}, [cls.container, className])}>
                <ChartControls
                    className={chartControlsClassName}
                    maxDate={MAX_DATE}
                    minDate={MIN_DATE}
                    size={chartControlsSize}
                    calendarOffset={calendarOffset}
                    onChange={(timeFrame: QueryFilter) => {
                        setSelectedTimeFrame(timeFrame);
                    }}
                />
                {showChart ? (
                    <div
                        className={classNames(cls.chartContainer, {}, [chartContainerClassName])}
                        onMouseEnter={() => {
                            setIsTooltipShown(true);
                        }}
                        onMouseLeave={() => {
                            setIsTooltipShown(false);
                        }}
                    >
                        <SkeletonWrapper width="100%" height="100%" isLoading={isLoading}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                    margin={{
                                        top: 16,
                                        left: chartOffset,
                                        right: 0,
                                    }}
                                    width={500}
                                    height={300}
                                    data={chartData}
                                >
                                    <defs>
                                        <linearGradient x1={0} x2={0} y1={0} y2={2} id="colorRub">
                                            <stop offset="25%" stopColor="#22BA72" />
                                            <stop offset="50%" stopColor="#fff" />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid vertical={false} strokeDasharray="10 10" />
                                    <XAxis
                                        className={chartTicksClassName}
                                        tickFormatter={(tick) => {
                                            return getXAxisLabel(tick);
                                        }}
                                        ticks={ticks}
                                        tickMargin={16}
                                        axisLine={false}
                                        tickLine={false}
                                        dataKey="quotesDate"
                                    />
                                    <YAxis
                                        className={chartTicksClassName}
                                        tickFormatter={(tick) => {
                                            return tick.toLocaleString('ru-ru');
                                        }}
                                        axisLine={false}
                                        tickLine={false}
                                        domain={[min, max]}
                                        tickMargin={yAxisOffset}
                                    />
                                    <Area
                                        type="monotone"
                                        fillOpacity={1}
                                        dataKey="quote"
                                        strokeWidth={2}
                                        stroke="#21BA72"
                                        fill="url(#colorRub)"
                                    />
                                    <Tooltip
                                        content={(props) => {
                                            if (!isTooltipShown) {
                                                return null;
                                            }

                                            const data = props.payload;

                                            if (!data || !Array.isArray(data)) {
                                                return null;
                                            }

                                            return (
                                                <div className={cls.tooltip}>
                                                    <p>
                                                        Стоимость юнита{' '}
                                                        <span className={cls.value}>
                                                            {data[0]?.value?.toLocaleString('ru-ru')} ₽
                                                        </span>
                                                    </p>
                                                    {getChartTooltipDate(props.label)}
                                                </div>
                                            );
                                        }}
                                        active={true}
                                        cursor={{ stroke: '#21BA72', strokeWidth: 1 }}
                                    />
                                    <Legend
                                        content={
                                            <div className={classNames(cls.legend, {}, [legendClassName])}>
                                                <div className={cls.legendItem}>
                                                    <div
                                                        className={classNames(cls.baseSquare, {}, [
                                                            cls.rubSquare,
                                                            squareClassName,
                                                        ])}
                                                    ></div>
                                                    <span>RUB</span>
                                                </div>
                                            </div>
                                        }
                                        align="left"
                                        iconType="square"
                                        layout="horizontal"
                                        wrapperStyle={{ paddingTop: 4, paddingLeft: legendPaddingLeft }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </SkeletonWrapper>
                    </div>
                ) : (
                    <DataNotFound />
                )}
            </div>
        </div>
    );
};
